import type { ButtonHTMLAttributes, ReactElement } from 'react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@doger/ui/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@doger/ui/components/ui/dialog';
import { ExternalLink, Link } from '@doger/ui/components/ui/link';
import { Separator } from '@doger/ui/components/ui/separator';

import { LogAmplitudeEventOnMount } from '@doger/chat/shared/analytics';

import { ReactComponent as MailFill } from './mail_fill.svg';
import { ReactComponent as SendPlaneFill } from './send_plane_fill.svg';
import styles from './support-modal.module.scss';

type SupportModalProps = {
  children: ReactElement<
    Pick<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>
  >;
};

export const SupportModal = memo(({ children }: SupportModalProps) => {
  const { t } = useTranslation();

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>

      <DialogContent className={styles.modal}>
        <LogAmplitudeEventOnMount event='Contact Support Modal Viewed' />

        <DialogHeader>
          <DialogTitle>{t('support.modal.title')}</DialogTitle>

          <DialogDescription>
            {t('support.modal.description')}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className={styles.body}>
          <Separator className={styles.separator} orientation='horizontal' />

          <div className={styles.contacts}>
            <Button variant='contained' palette='ghost' size='lg' asChild>
              <ExternalLink
                className={styles.contact}
                href='https://t.me/DogeRL2S'
                palette='secondary'
              >
                <SendPlaneFill className={styles.icon} />

                <span className={styles.contactText}>@DogeRL2S</span>
              </ExternalLink>
            </Button>

            <Button variant='contained' palette='ghost' size='lg' asChild>
              <Link
                className={styles.contact}
                to='mailto:doger@layer2state.com'
                palette='secondary'
              >
                <MailFill className={styles.icon} />

                <span className={styles.contactText}>
                  doger@layer2state.com
                </span>
              </Link>
            </Button>
          </div>

          <DialogClose asChild>
            <Button
              className={styles.close}
              variant='contained'
              palette='primary'
              size='lg'
            >
              Got it
            </Button>
          </DialogClose>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
});

SupportModal.displayName = 'SupportModal';
